// @ts-nocheck
import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { FaExchangeAlt } from "react-icons/fa"

import { type ApiError, type WritingMetadataPublic, WritingsService } from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import { Field } from "../ui/field"

interface EditWritingMetadataProps {
  writingMetadata: WritingMetadataPublic
}

interface WritingMetadataUpdateForm {
  title: string
  description: string
}

const EditWritingMetadata = ({ writingMetadata }: EditWritingMetadataProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WritingMetadataUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      title: writingMetadata.title,
      description: writingMetadata.description,
    },
  })

  const mutation = useMutation({
    mutationFn: (data: WritingMetadataUpdateForm) =>
      WritingsService.updateWritingMetadata({ id: writingMetadata.writing_id, requestBody: data }),
    onSuccess: () => {
      showSuccessToast("Writing updated successfully.")
      reset()
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["writings"] })
    },
  })

  const onSubmit: SubmitHandler<WritingMetadataUpdateForm> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <DialogRoot
        size={{ base: "xs", md: "md" }}
        placement="center"
        open={isOpen}
        onOpenChange={({ open }) => {
          setIsOpen(open)
          if (open) {
            // 다이얼로그가 열릴 때 기존 테이블 데이터를 폼에 채워 넣음
            reset({
              title: writingMetadata.title,
              description: writingMetadata.description,
            })
          }
        }}
      >
      <DialogTrigger asChild>
        <Button variant="ghost">
          <FaExchangeAlt fontSize="16px" />
          메모 수정
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>메모 수정</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>아래 메모 세부 정보를 수정하세요.</Text>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.title}
                errorText={errors.title?.message}
                label="메모 제목"
              >
                <Input
                  id="title"
                  {...register("title", {
                    required: "메모 제목을 입력해주세요.",
                  })}
                  placeholder="메모 제목"
                  type="text"
                />
              </Field>

              <Field
                invalid={!!errors.description}
                errorText={errors.description?.message}
                label="설명"
              >
                <Input
                  id="description"
                  {...register("description")}
                  placeholder="설명"
                  type="text"
                />
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  취소
                </Button>
              </DialogActionTrigger>
              <Button variant="solid" type="submit" loading={isSubmitting}>
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
          </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default EditWritingMetadata
